/**
 * Purchase Event Preprocessor
 * Handles all preprocessing/transformation logic for purchase events
 * Components should pass raw data, and this utility handles all calculations
 */

import { Product, Coupon } from '../../../types';
import { SignupInvoicePreview } from '../../../services/membershipApi/signup/previewInvoice';

export interface RawPurchaseData {
    product: Product;
    priceId: string;
    coupon?: Coupon | null;
    giftCode?: any | null;
    invoicePreview?: SignupInvoicePreview | null;
}

export interface ProcessedPurchaseData {
    // Purchase transaction data
    transaction_id: string;
    value: number;
    currency: string;
    
    // Product data
    membership_tier: string;
    product_name: string;
    product_id: string;
    price_id: string;
    billing_cycle: 'monthly' | 'annual';
    
    // Pricing details
    price_amount_cents: number;
    original_price_cents: number;
    
    // Discount/coupon info
    has_gift_code: boolean;
    has_coupon: boolean;
    coupon_id?: string;
    discount_amount_cents: number;
    
    // Item structure for ecommerce (Firebase, etc.)
    items: Array<{
        item_id: string;
        item_name: string;
        item_category: string;
        price: number;
        quantity: number;
        currency: string;
    }>;
}

/**
 * Determine billing cycle from price ID
 */
const getBillingCycleFromPriceId = (product: Product, priceId: string): 'monthly' | 'annual' => {
    if (product.pricing?.monthly?.id === priceId) {
        return 'monthly';
    }
    if (product.pricing?.annual?.id === priceId) {
        return 'annual';
    }
    // Fallback: try to infer from available pricing
    if (product.pricing?.monthly && !product.pricing?.annual) {
        return 'monthly';
    }
    if (product.pricing?.annual && !product.pricing?.monthly) {
        return 'annual';
    }
    // Default to monthly if uncertain
    return 'monthly';
};

/**
 * Process raw purchase data into analytics-ready format
 * Handles all calculations, transformations, and data enrichment
 * Completely safe - handles null/undefined inputs gracefully, never throws
 */
export const processPurchaseEvent = (data: RawPurchaseData | null | undefined): ProcessedPurchaseData => {
    // Safe defaults for all fields
    const safeDefaults: ProcessedPurchaseData = {
        transaction_id: `txn_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
        value: 0,
        currency: 'USD',
        membership_tier: '',
        product_name: '',
        product_id: '',
        price_id: '',
        billing_cycle: 'monthly',
        price_amount_cents: 0,
        original_price_cents: 0,
        has_gift_code: false,
        has_coupon: false,
        discount_amount_cents: 0,
        items: [{
            item_id: '',
            item_name: '',
            item_category: 'membership',
            price: 0,
            quantity: 1,
            currency: 'USD',
        }],
    };

    try {
        // Validate input
        if (!data || !data.product || !data.priceId) {
            return safeDefaults;
        }

        const { product, priceId, coupon, giftCode, invoicePreview } = data;
        
        // Safely determine billing cycle
        let billingCycle: 'monthly' | 'annual';
        try {
            billingCycle = getBillingCycleFromPriceId(product, priceId);
        } catch (error) {
            // Fallback to monthly if detection fails
            billingCycle = 'monthly';
        }
        
        // Get selected pricing
        const selectedPricing = billingCycle === 'monthly' 
            ? product.pricing?.monthly 
            : product.pricing?.annual;
        
        // Use invoice preview data if available (includes tax and final total)
        // Otherwise calculate from product pricing
        let finalValue: number;
        let currency: string;
        let priceAmountCents: number;
        let discountAmountCents: number;
        
        if (invoicePreview && invoicePreview.success && invoicePreview.total > 0) {
            // Use preview data (includes tax, discounts, etc.)
            finalValue = invoicePreview.total / 100; // Convert cents to dollars
            currency = typeof invoicePreview.currency === 'string'
                ? invoicePreview.currency.toUpperCase()
                : 'USD'; // Normalize to uppercase
            priceAmountCents = invoicePreview.subtotal || 0;
            // Calculate discount from preview (use discountAmount if available, otherwise calculate from discounts array)
            if (invoicePreview.discountAmount !== null && invoicePreview.discountAmount !== undefined) {
                discountAmountCents = invoicePreview.discountAmount;
            } else if (invoicePreview.discounts && Array.isArray(invoicePreview.discounts)) {
                discountAmountCents = invoicePreview.discounts.reduce((sum, discount) => sum + (discount.amount || 0), 0);
            } else {
                discountAmountCents = 0;
            }
        } else {
            // Fallback to calculating from product pricing
            try {
                priceAmountCents = selectedPricing?.amountCents || 0;
                if (coupon && selectedPricing && priceAmountCents > 0) {
                    try {
                        const discountCents = coupon.percent_off 
                            ? Math.round(priceAmountCents * (Math.max(0, Math.min(100, coupon.percent_off || 0)) / 100))
                            : (Math.max(0, coupon.amount_off || 0));
                        priceAmountCents = Math.max(0, priceAmountCents - discountCents);
                        discountAmountCents = discountCents;
                    } catch (error) {
                        // If discount calculation fails, use original price
                        priceAmountCents = selectedPricing?.amountCents || 0;
                        discountAmountCents = 0;
                    }
                } else {
                    discountAmountCents = 0;
                }
            } catch (error) {
                // If price calculation fails, use 0
                priceAmountCents = 0;
                discountAmountCents = 0;
            }
            
            // Convert to dollars (without tax since we don't have preview)
            finalValue = isNaN(priceAmountCents) ? 0 : Math.max(0, priceAmountCents / 100);
            currency = selectedPricing?.currency || 'USD';
        }
        
        // Generate unique transaction ID (safe - Date.now() and Math.random() never throw)
        const transactionId = `txn_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
        
        // Safely get product identifiers
        const productName = (product.title || product.name || '').toString();
        const tierId = (product.tierId || product.id || '').toString();
        const safePriceId = (priceId || '').toString();
        
        // Product ID format: tierId_billingCycle (e.g., "tier1_monthly")
        const productId = tierId ? `${tierId}_${billingCycle}` : safePriceId;
        
        // Item ID should also use tierId_billingCycle format
        const itemId = productId;
        
        // Calculate original price (before discounts)
        const originalPriceCents = selectedPricing?.amountCents || 0;
        
        // Calculate item price for ecommerce tracking
        // For proper revenue attribution in GA4/Firebase Analytics:
        // - Item price should be the final price per item (after discounts, including tax)
        // - For single-item transactions, item price should match transaction value
        // This ensures: item_price * quantity = transaction_value (GA4 requirement)
        const itemPrice = finalValue;
        
        return {
            // Purchase transaction data
            transaction_id: transactionId,
            value: finalValue,
            currency: currency,
            
            // Product data
            membership_tier: productName,
            product_name: productName,
            product_id: productId,
            price_id: safePriceId,
            billing_cycle: billingCycle,
            
            // Pricing details
            price_amount_cents: priceAmountCents,
            original_price_cents: originalPriceCents,
            
            // Discount/coupon info
            has_gift_code: !!giftCode,
            has_coupon: !!coupon,
            coupon_id: coupon?.id?.toString(),
            discount_amount_cents: discountAmountCents,
            
            // Item structure for ecommerce (Firebase, Facebook, TikTok, etc.)
            // Always populated with complete product information
            items: [{
                item_id: itemId,
                item_name: productName,
                item_category: 'membership',
                price: itemPrice,
                quantity: 1,
                currency: currency,
            }],
        };
    } catch (error) {
        // If anything goes wrong, return safe defaults
        // This ensures analytics never breaks the app
        return safeDefaults;
    }
};

